// `/meus-quadrinhos/novo` — ESCOLHER O FORMATO DA PÁGINA.
//
// Esta tela já existia: era o estado `escolhendoFormato` dentro do `Dashboard`, aberto pelo balão
// "Novo quadrinho". O que mudou foi ela ganhar endereço — dá para chegar aqui do cabeçalho, da
// biblioteca vazia, da home, e dá para voltar com o botão do navegador sem sair do acervo.
//
// O conteúdo é o mesmo, e as duas decisões que ele carrega continuam valendo (as duas foram
// medidas no T1, e os comentários originais estão preservados abaixo):
//
//   · a amostra tem a PROPORÇÃO real do formato, porque "1200 × 400" não diz nada a quem tem
//     oito anos e um retângulo largo e baixo sim;
//   · a folha nasce COM os quadros que o nome dela promete (`comicComGradeInicial`), porque
//     escolher "Página de revista — A4, cabe de 4 a 6 quadros" e abrir uma folha vazia é o
//     rótulo mentindo.
//
// ── O QUE MUDOU NO FLUXO ────────────────────────────────────────────────────────────────────
//
// Antes, escolher um formato chamava `setCurrentComic(comicComGradeInicial(id))` e a MESMA rota
// passava a desenhar o editor. O quadrinho novo não tinha id — ele só ganha um ao ser salvo —,
// então não havia endereço para ele.
//
// Continua não havendo, e isso é do formato de dado, não desta tela: um quadrinho que nunca foi
// salvo não existe no Firestore. Por isso o editor aceita ser aberto de dois jeitos — por id
// (`/meus-quadrinhos/:id/editar`) ou com um rascunho entregue na navegação, que é o caminho
// daqui. Ver `editar-quadrinho.tsx`.

import { useLocation, useNavigate } from "react-router-dom";
import GuardaDoAcervo from "./guarda";
import Nav from "../../components/nav";
import { FormatoPagina, PADROES_PAGINA } from "../../api/documento.types";
import AmostraDoFormato from "./amostra-do-formato";
import { paraOndeOVoltarLeva, SAIDA_PARA_O_ACERVO } from "./para-onde-o-voltar-leva";

import "../dashboard/dashboard.scss";

import { useTituloDaAba } from "../../hooks/use-titulo-da-aba";
import { useState } from "react";
// A CONTA DO TAMANHO DA AMOSTRA SAIU DAQUI em 03/10 (T12.5): ela virou `folha()` dentro de
// `amostra-do-formato.tsx`, junto com o desenho que a usa. O piso subiu de 6 para 14 px no
// caminho, e o motivo está lá — a medição achou a amostra mais fina com 15 pt de largura.

/** O teto do nome, e ele é o mesmo que o editor cobrava no diálogo. Uma só regra, um só número. */
export const LETRAS_DO_NOME = 30;

function NovoQuadrinho() {
  // T8.39 · a tela que pergunta o formato nao se identificava na aba.
  useTituloDaAba("Novo quadrinho");
  const navigate = useNavigate();
  // T12.3 · O "voltar" desta tela levava SEMPRE ao acervo, e dá para chegar aqui de quatro
  // lugares. A decisão está em `para-onde-o-voltar-leva.ts`, com o motivo de olhar o histórico em
  // vez de adivinhar.
  const saida = paraOndeOVoltarLeva(useLocation().key);

  /**
   * O NOME, PEDIDO AQUI E NÃO NA HORA DE SALVAR — T8.29.
   *
   * Ele: "Pedir junto com o formato evita o modal e já permite o salvamento automático." As duas
   * consequências são reais, e a segunda é a que importa: o salvamento automático precisa de um nome
   * para ter o que gravar, e antes disto o nome só existia depois do primeiro Salvar manual.
   *
   * COMEÇA VAZIO E NÃO COM "Sem título": um campo pré-preenchido com nome genérico é um campo que
   * ninguém troca, e a biblioteca enche de "Sem título (2)". Vazio, a pessoa escreve ou pula.
   */
  const [nome, setNome] = useState("");
  const [erroDoNome, setErroDoNome] = useState<string | null>(null);

  function seguir(formato: string) {
    const limpo = nome.trim();
    if (limpo.length > LETRAS_DO_NOME) {
      // A frase conta LETRAS e não caracteres: uma criança conta letras.
      setErroDoNome(
        `O nome ficou com ${limpo.length} letras, e cabem ${LETRAS_DO_NOME}. Escolha um mais curto.`
      );
      return;
    }
    setErroDoNome(null);
    // O nome pode ir vazio: quem não quer nomear agora nomeia ao salvar, como antes. O que mudou é
    // que agora DÁ para nomear no começo — não que seja obrigatório.
    navigate("/meus-quadrinhos/rascunho", { state: { formato, nome: limpo } });
  }

  return (
    <GuardaDoAcervo>
      {() => (
        <div className="dashboard-container">
          <Nav />

          <div className="escolha-formato escolha-formato-pagina" role="group" aria-label="Formato da página">
            <h1>De que tamanho vai ser a página?</h1>
            <p>Dá para mudar depois, em “Redimensionar”.</p>

            {/* T8.29 · o nome vem ANTES do formato na leitura, porque é a pergunta mais fácil das duas
                — e porque quem já sabe o nome da história escreve sem pensar. */}
            <div className="novo-campo-nome">
              <label htmlFor="nome-do-quadrinho">Como vai se chamar? (dá para deixar em branco)</label>
              <input
                id="nome-do-quadrinho"
                type="text"
                maxLength={LETRAS_DO_NOME + 10}
                autoComplete="off"
                placeholder="A Bia e o gato"
                value={nome}
                onChange={(e) => {
                  setNome(e.target.value);
                  if (erroDoNome) setErroDoNome(null);
                }}
              />
              {erroDoNome && (
                <span className="novo-campo-erro" role="alert">
                  {erroDoNome}
                </span>
              )}
            </div>

            <div className="escolha-formato-opcoes">
              {(Object.keys(PADROES_PAGINA) as (keyof typeof PADROES_PAGINA)[]).map((id) => {
                const f = PADROES_PAGINA[id];
                return (
                  <button
                    key={id}
                    type="button"
                    className="formato-opcao"
                    onClick={() => {
                      // Era `getEmptyComic(largura, altura)`: a folha saía VAZIA em todos os
                      // seis, e o rótulo prometia grade em quatro deles.
                      seguir(id);
                    }}
                  >
                    {/* ── T12.5 · A AMOSTRA MOSTRA A GRADE DE VERDADE ────────────────────────
                        A proporção real continua valendo, e a decisão de 12/09 também: um rótulo
                        que diz "1200 × 400" não ajuda quem tem oito anos. O que mudou é que a
                        amostra deixou de ser um retângulo VAZIO.

                        Medido em 03/10: **4 amostras distintas de 6**, porque página de revista,
                        grade livre e capa têm a mesma folha A4 e, só com proporção, saem
                        idênticas — e a mais fina tinha **15 pt** de largura, que é o *"traço quase
                        invisível"* da letra dele.

                        Agora ela desenha `PADROES_PAGINA[id].grade`, que é a MESMA grade com que a
                        folha nasce. O que a criança vê na miniatura é literalmente o que vai
                        aparecer quando a folha abrir. Ver `amostra-do-formato.tsx`. */}
                    <AmostraDoFormato formato={id as FormatoPagina} />
                    <span className="formato-texto">
                      <strong>{f.rotulo}</strong>
                      <small>{f.explicacao}</small>
                      {/* ── A DECISÃO QUE EU TOMEI, E ELA REABRE UMA DECISÃO MEDIDA ────────────
                          Dois nós esperavam uma frase do operador: *"não sei que tamanho a página
                          vai ter"* (T10.4) pede o número, e a decisão de 12/09 tirou o número de
                          propósito — *"um rótulo «1200 × 400» não ajuda quem tem oito anos, e um
                          retângulo largo e baixo sim"*, e com teto de altura o webtoon e o A4
                          saíam do mesmo tamanho.

                          AS DUAS ESTÃO CERTAS, e não se contradizem: a de 12/09 é sobre o que
                          RESPONDE a pergunta ("qual a forma desta folha?"), e a de hoje é sobre
                          uma segunda pergunta que um adulto também faz ("de que tamanho?").
                          Decidi: **a amostra continua respondendo primeiro, e o número entra
                          abaixo, pequeno** — quem tem oito anos olha o desenho, quem quer o
                          número o acha sem procurar.

                          O CUSTO DE EU ESTAR ERRADO: tirar é apagar esta linha. */}
                      <small className="formato-tamanho">
                        {f.largura} &times; {f.altura} pontos
                      </small>
                    </span>
                  </button>
                );
              })}
            </div>

            {/* ── T12.3 · DUAS SAÍDAS, E A PRIMEIRA É A QUE ELE PROCUROU ────────────────────
                Era um botão só, dizendo "Voltar para meus quadrinhos" de qualquer lugar — e no
                caso dele o acervo estava VAZIO, então o botão de voltar levava a uma tela sem
                nada. Agora o primeiro volta de onde a pessoa veio (ou ao início, quando esta é a
                primeira tela da visita), e o acervo continua a um clique, com o nome dele. */}
            <div className="escolha-formato-saidas">
              <button
                type="button"
                className="botao-secundario mq-voltar"
                onClick={() => (saida.destino === "tras" ? navigate(-1) : navigate(saida.destino))}
              >
                {saida.rotulo}
              </button>
              <button
                type="button"
                className="botao-secundario mq-voltar"
                onClick={() => navigate(SAIDA_PARA_O_ACERVO.destino)}
              >
                {SAIDA_PARA_O_ACERVO.rotulo}
              </button>
            </div>
          </div>
        </div>
      )}
    </GuardaDoAcervo>
  );
}

export default NovoQuadrinho;
